import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "../utils";
import { Icon } from "../icon";
import { iconButtonVariants } from "./icon-button.variants";
import type { IconButtonProps } from "./icon-button.types";

/**
 * Icon Button Component
 *
 * V&A Waterfront Design System - Icon Button
 * Figma: https://www.figma.com/design/CV2ePEn9S0SSZ7T3AAPqF4/V-A-Waterfront---Website---Foundation-Design-System?node-id=9839-4058
 *
 * Icon-only buttons for actions like close, navigation, or toolbar controls.
 *
 * @example
 * // Basic usage with icon name
 * <IconButton icon="close" aria-label="Close dialog" />
 *
 * // With variant
 * <IconButton icon="chevron-left" variant="white" aria-label="Previous" />
 *
 * // Small size for compact layouts
 * <IconButton icon="menu" size="sm" aria-label="Open menu" />
 *
 * // With custom icon via children
 * <IconButton aria-label="Custom action">
 *   <CustomIcon />
 * </IconButton>
 *
 * // As anchor for navigation
 * <IconButton asChild variant="grey" aria-label="Go to home">
 *   <a href="/">
 *     <Icon name="home" />
 *   </a>
 * </IconButton>
 */
const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>((t0, ref) => {
  const $ = _c(27);
  let children;
  let className;
  let disabled;
  let icon;
  let props;
  let size;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      asChild: t1,
      icon,
      children,
      disabled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = disabled;
    $[4] = icon;
    $[5] = props;
    $[6] = size;
    $[7] = t1;
    $[8] = variant;
  } else {
    children = $[1];
    className = $[2];
    disabled = $[3];
    icon = $[4];
    props = $[5];
    size = $[6];
    t1 = $[7];
    variant = $[8];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? Slot : "button";
  const t2 = asChild ? undefined : "button";
  let t3;
  if ($[9] !== className || $[10] !== size || $[11] !== variant) {
    t3 = cn(iconButtonVariants({
      variant,
      size,
      className
    }));
    $[9] = className;
    $[10] = size;
    $[11] = variant;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const t4 = asChild ? undefined : disabled;
  const t5 = disabled || undefined;
  const t6 = disabled || undefined;
  const t7 = disabled ? -1 : undefined;
  let t8;
  if ($[13] !== children || $[14] !== icon) {
    t8 = icon ? <Icon name={icon} /> : children;
    $[13] = children;
    $[14] = icon;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== Comp || $[17] !== props || $[18] !== ref || $[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7 || $[25] !== t8) {
    t9 = <Comp ref={ref} data-slot="icon-button" type={t2} className={t3} disabled={t4} aria-disabled={t5} data-disabled={t6} tabIndex={t7} {...props}>{t8}</Comp>;
    $[16] = Comp;
    $[17] = props;
    $[18] = ref;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
});
IconButton.displayName = "IconButton";
export { IconButton };